// Portfolio Administration v1.0 — cross-portfolio Loans list page at
// /portfolio-admin/loans. There is intentionally no "+ Add loan" affordance —
// Loans are created automatically by the Deal Flow Closing flow
// (Underwriting spec §11.5 / §19), never by hand here.
//
// Loans-list overhaul:
//   • Summary strip (five-second read over the active filters).
//   • Grade column from the batch health-grades endpoint (the same server
//     computation as the loan dashboard) + real days late + next payment.
//   • "My loans" default view for staff who hold assignment pills
//     (first visit probes "mine", falls back to All when empty; the choice
//     persists per user in localStorage).
//   • Days-late buckets filter CLIENT-SIDE over real days (the old
//     AccountState proxy is gone).
//   • Group-by-portfolio toggle (?group=portfolio — the legacy /portfolios
//     redirect target) with per-portfolio rollups.
//   • Bulk bar wired to Assign B1 + Move to portfolio.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import {
  useLoansList,
  usePortfolios,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { useLoanGrades } from "@/hooks/portfolioAdmin/useLoanHealth";
import { useToast } from "@/design-system/components/ui";
import { PlaceholderPage } from "@/pages/core/PlaceholderPage";
import { RegistryListLayout } from "@/components/portfolioAdmin/registry/RegistryListLayout";
import { LoansListFilters } from "@/components/portfolioAdmin/loans/LoansListFilters";
import {
  LoansListTable,
  type LoanListRow,
} from "@/components/portfolioAdmin/loans/LoansListTable";
import { LoansSummaryStrip } from "@/components/portfolioAdmin/loans/LoansSummaryStrip";
import { AssignB1Modal } from "@/components/portfolioAdmin/loans/AssignB1Modal";
import { MoveToPortfolioModal } from "@/components/portfolioAdmin/portfolios/MoveToPortfolioModal";
import { cn } from "@/design-system/lib/utils";
import { daysLateBucket } from "@/api/portfolioAdmin/types";

const VIEW_STORAGE_KEY = "phx.loans.view";
const GROUP_STORAGE_KEY = "phx.loans.groupByPortfolio";

function readStored(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* localStorage unavailable — ignore */
  }
}

function arrayParam(params: URLSearchParams, key: string): string[] {
  const raw = params.getAll(key);
  if (raw.length === 0) return [];
  return raw.flatMap((v) => v.split(",")).filter(Boolean);
}

export default function LoansListPage() {
  const flagOn = useFeatureFlag("PortfolioAdminV1");
  if (!flagOn) {
    return (
      <PlaceholderPage
        phase="Phase 3"
        title="Loan Servicing"
        description="Portfolio, payments, waterfalls, covenants, seasoning."
      />
    );
  }
  return <LoansListPageInner />;
}

function LoansListPageInner() {
  const auth = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const portfolioIds = arrayParam(params, "portfolioId");
  const accountStates = arrayParam(params, "accountState");
  const daysLateBuckets = arrayParam(params, "daysLate");
  const assignedB1UserId = params.get("assignedB1UserId") ?? "";
  const assignedB1UserName = params.get("assignedB1Name") ?? "";
  const search = params.get("search") ?? "";
  const groupByPortfolio =
    params.get("group") === "portfolio" || readStored(GROUP_STORAGE_KEY) === "1";

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkModal, setBulkModal] = useState<"assign-b1" | "move" | null>(null);

  // "My loans" default view: first visit (no stored choice) probes the
  // caller's assignments; an empty result falls back to All loans. The
  // resolved choice persists per user thereafter.
  const initialView = useRef(readStored(VIEW_STORAGE_KEY));
  const probing = useRef(initialView.current === null);
  const [view, setViewState] = useState<"mine" | "all">(
    initialView.current === "all" ? "all" : "mine",
  );

  const setView = useCallback((next: "mine" | "all") => {
    probing.current = false;
    setViewState(next);
    writeStored(VIEW_STORAGE_KEY, next);
    setSelectedIds([]);
  }, []);

  const canBulkAct =
    auth.hasRoleCode("E1") ||
    auth.hasRoleCode("ADMIN") ||
    auth.hasPermission("Loans.Manage");

  const writeParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      const next = new URLSearchParams(params);
      mutate(next);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const setMulti = (key: string) => (values: string[]) =>
    writeParams((p) => {
      p.delete(key);
      values.forEach((v) => p.append(key, v));
    });

  const setSearch = (next: string) =>
    writeParams((p) => {
      if (next) p.set("search", next);
      else p.delete("search");
    });

  const setAssignedB1 = (userId: string | null, displayName: string) =>
    writeParams((p) => {
      if (userId) {
        p.set("assignedB1UserId", userId);
        p.set("assignedB1Name", displayName);
      } else {
        p.delete("assignedB1UserId");
        p.delete("assignedB1Name");
      }
    });

  const setGroupByPortfolio = (next: boolean) => {
    writeStored(GROUP_STORAGE_KEY, next ? "1" : "0");
    writeParams((p) => {
      if (next) p.set("group", "portfolio");
      else p.delete("group");
    });
  };

  const clearFilters = () =>
    writeParams((p) => {
      p.delete("portfolioId");
      p.delete("accountState");
      p.delete("daysLate");
      p.delete("assignedB1UserId");
      p.delete("assignedB1Name");
      p.delete("search");
    });

  const listQuery = useLoansList({
    search: search || undefined,
    portfolioIds: portfolioIds.length ? portfolioIds : undefined,
    accountStates: accountStates.length ? accountStates : undefined,
    assignedB1UserId: assignedB1UserId || undefined,
    assignedToMe: view === "mine" ? true : undefined,
  });

  // First-visit probe resolution: an empty "mine" view flips to All.
  useEffect(() => {
    if (!probing.current || view !== "mine" || !listQuery.data) return;
    probing.current = false;
    if (listQuery.data.length === 0) {
      setViewState("all");
      writeStored(VIEW_STORAGE_KEY, "all");
    } else {
      writeStored(VIEW_STORAGE_KEY, "mine");
    }
  }, [listQuery.data, view]);

  const loans = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  const portfolios = usePortfolios();
  const portfolioNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of portfolios.data ?? []) map.set(p.id, p.name);
    return map;
  }, [portfolios.data]);

  const portfolioOptions = useMemo(
    () =>
      (portfolios.data ?? [])
        .filter((p) => p.status === "Open" || portfolioIds.includes(p.id))
        .map((p) => ({ value: p.id, label: p.name })),
    [portfolios.data, portfolioIds],
  );

  const loanIds = useMemo(() => loans.map((l) => l.id), [loans]);
  const gradesQuery = useLoanGrades(loanIds);
  const gradeByLoanId = useMemo(() => {
    const map = new Map<string, NonNullable<typeof gradesQuery.data>[number]>();
    for (const g of gradesQuery.data ?? []) map.set(g.loanId, g);
    return map;
  }, [gradesQuery.data]);
  const gradesLoading = gradesQuery.isLoading && loanIds.length > 0;

  const rows = useMemo<LoanListRow[]>(() => {
    let result = loans.map((loan) => ({
      loan,
      grade: gradeByLoanId.get(loan.id),
      portfolioName: loan.portfolioId
        ? (portfolioNameById.get(loan.portfolioId) ?? null)
        : null,
    }));
    // Days-late buckets apply client-side over the REAL schedule-derived
    // days from the batch payload (no grade row yet → treated as 0 days).
    if (daysLateBuckets.length > 0 && !gradesLoading) {
      result = result.filter((r) =>
        daysLateBuckets.includes(daysLateBucket(r.grade?.daysDelinquent ?? 0)),
      );
    }
    if (groupByPortfolio) {
      // Stable order: portfolios alphabetically, "No portfolio" last.
      result = [...result].sort((a, b) => {
        const an = a.portfolioName ?? "\uffff";
        const bn = b.portfolioName ?? "\uffff";
        return an.localeCompare(bn) || a.loan.loanNumber.localeCompare(b.loan.loanNumber);
      });
    }
    return result;
  }, [
    loans,
    gradeByLoanId,
    portfolioNameById,
    daysLateBuckets,
    gradesLoading,
    groupByPortfolio,
  ]);

  const summary = useMemo(() => {
    const gradeCounts: Record<string, number> = {};
    let delinquent = 0;
    for (const r of rows) {
      const g = r.grade?.grade.grade;
      if (g) gradeCounts[g] = (gradeCounts[g] ?? 0) + 1;
      if ((r.grade?.daysDelinquent ?? 0) > 0) delinquent++;
    }
    return {
      loanCount: rows.length,
      totalCurrentBalance: rows.reduce((sum, r) => sum + r.loan.currentBalance, 0),
      delinquentCount: gradesLoading ? null : delinquent,
      gradeCounts: gradesLoading ? null : gradeCounts,
    };
  }, [rows, gradesLoading]);

  const anySelectedCurrentlyAssigned = useMemo(
    () =>
      rows.some(
        (r) => selectedIds.includes(r.loan.id) && r.loan.portfolioId !== null,
      ),
    [rows, selectedIds],
  );

  const countSummary = listQuery.isLoading
    ? "Loading loans…"
    : `Showing ${rows.length} ${rows.length === 1 ? "loan" : "loans"}`;

  return (
    <RegistryListLayout
      title="Loans"
      testIdPrefix="loans"
      toolbar={
        <div className="flex flex-wrap items-center gap-space-3">
          <div
            className="inline-flex rounded-md border border-border-light overflow-hidden"
            role="group"
            aria-label="Loan view"
          >
            <ViewToggleButton
              active={view === "mine"}
              onClick={() => setView("mine")}
              testId="loans-view-mine"
            >
              My loans
            </ViewToggleButton>
            <ViewToggleButton
              active={view === "all"}
              onClick={() => setView("all")}
              testId="loans-view-all"
            >
              All loans
            </ViewToggleButton>
          </div>
          <label className="inline-flex items-center gap-space-1 text-small text-onyx cursor-pointer">
            <input
              type="checkbox"
              checked={groupByPortfolio}
              onChange={(e) => setGroupByPortfolio(e.target.checked)}
              data-testid="loans-group-toggle"
            />
            Group by portfolio
          </label>
        </div>
      }
      filters={
        <LoansListFilters
          portfolioIds={portfolioIds}
          portfolioOptions={portfolioOptions}
          accountStates={accountStates}
          daysLateBuckets={daysLateBuckets}
          assignedB1UserId={assignedB1UserId}
          assignedB1UserName={assignedB1UserName}
          search={search}
          onPortfolioIdsChange={setMulti("portfolioId")}
          onAccountStatesChange={setMulti("accountState")}
          onDaysLateBucketsChange={setMulti("daysLate")}
          onAssignedB1Change={setAssignedB1}
          onSearchChange={setSearch}
          onClear={clearFilters}
        />
      }
      bulkActions={
        canBulkAct && selectedIds.length > 0 ? (
          <div
            className="flex items-center gap-space-2 bg-gold-highlight/10 border border-gold-shadow rounded-md px-space-3 py-space-2"
            data-testid="loans-bulk-actions"
          >
            <span className="text-small font-semibold">
              {selectedIds.length} selected
            </span>
            <button
              type="button"
              className="text-small text-gold-shadow hover:underline"
              data-testid="loans-bulk-assign-b1"
              onClick={() => setBulkModal("assign-b1")}
            >
              Assign B1
            </button>
            <button
              type="button"
              className="text-small text-gold-shadow hover:underline"
              data-testid="loans-bulk-move-portfolio"
              onClick={() => setBulkModal("move")}
            >
              Move to portfolio
            </button>
          </div>
        ) : undefined
      }
      countSummary={
        <span className="flex items-center gap-space-2">
          <span data-testid="loans-count-summary-text">{countSummary}</span>
          <span
            className="text-[10px] text-gray-mid italic"
            data-testid="loans-creation-note"
          >
            Loans are created automatically when a deal closes via Deal Flow → Closing.
          </span>
        </span>
      }
    >
      <div className="flex flex-col gap-space-3">
        <LoansSummaryStrip
          loanCount={summary.loanCount}
          totalCurrentBalance={summary.totalCurrentBalance}
          delinquentCount={summary.delinquentCount}
          gradeCounts={summary.gradeCounts}
        />
        <LoansListTable
          rows={rows}
          gradesLoading={gradesLoading}
          loading={listQuery.isLoading}
          error={listQuery.isError ? "Couldn't load loans. Try again." : null}
          selectable={canBulkAct}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          groupByPortfolio={groupByPortfolio}
        />
      </div>

      {bulkModal === "assign-b1" && (
        <AssignB1Modal
          loanIds={selectedIds}
          onClose={() => setBulkModal(null)}
          onDone={(count) => {
            setBulkModal(null);
            setSelectedIds([]);
            toast.success(`B1 assigned on ${count} ${count === 1 ? "loan" : "loans"}.`);
          }}
        />
      )}
      {bulkModal === "move" && (
        <MoveToPortfolioModal
          loanIds={selectedIds}
          anyCurrentlyAssigned={anySelectedCurrentlyAssigned}
          mode="move"
          onClose={() => setBulkModal(null)}
          onDone={(moved, skipped) => {
            setBulkModal(null);
            setSelectedIds([]);
            toast.success(
              `Moved ${moved} ${moved === 1 ? "loan" : "loans"}${skipped > 0 ? ` (${skipped} already there)` : ""}.`,
            );
          }}
        />
      )}
    </RegistryListLayout>
  );
}

function ViewToggleButton({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "px-space-3 py-space-1 text-small transition-colors duration-fast",
        active
          ? "bg-onyx text-white-pure font-semibold"
          : "bg-white-pure text-onyx hover:bg-white-warm",
      )}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
